import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import OtpInput from "react-otp-input";
import styles from "./transaction-pin-modal.module.scss";
import { SuccessScreen } from "../success-screen";
import CommonDialog from "../../../../common/components/dialog/CommonDialog";
import Button from "../../../../common/ekedc-components/button/Button";
import { SwitchButton } from "../../../../common/components/switch-button/SwitchButton";
import { SelectableItem } from "../../../../common/components/selectable-item/SelectableItem";
import { RootState } from "../../../../stores/store";
import { updateUserPin } from "../../../../stores/slices/current-user-slice";
import { useCreatePinMutation } from "../../../../service/customer-service-v2";
import { getErrorMessage } from "../../../../utils/error-handler";
import { describeError } from "../../../../services/business-analytics/customFields";
import { trackSetTransactionPinFailed, trackSetTransactionPinSuccess } from "../../../../services/business-analytics/transactionPinEvents";

export interface TransactionPinModalProps {
	/** Submit button text on the validate step, e.g. "Pay ₦5,000". */
	submitText?: string;
	/** Payment in progress (controls the submit button's loading state). */
	isSubmitting?: boolean;
	/**
	 * Called with the entered 4-digit PIN and the computed remoteTokenLoadForPayment value.
	 * remoteTokenLoadForPayment mirrors Flutter's _setRemoteTokenLoadingForPayment():
	 *   true  — remote token load toggle ON (use remote load for this payment)
	 *   null  — toggle OFF, "Don't Show Again" unchecked (ask again next time)
	 *   false — toggle OFF, "Don't Show Again" checked (stop asking)
	 * undefined when showRemoteTokenLoad is false.
	 */
	onSubmit: (pin: string, remoteTokenLoadForPayment?: boolean | null) => void | Promise<void>;
	onForgetPin: () => void;
	onClose: () => void;
	/** Show the Remote Token Load toggle + Don't Show Again option. */
	showRemoteTokenLoad?: boolean;
	/** Initial value of the Remote Token Load toggle (read from user's saved preference). */
	initialRemoteTokenLoad?: boolean;
}

const CrossIcon: React.FC = () => (
	<svg width="22" height="22" viewBox="0 0 22 22" fill="none">
		<path d="M5.5 5.5L16.5 16.5M16.5 5.5L5.5 16.5" stroke="var(--app-primary-text)" strokeWidth="1.5" strokeLinecap="round" />
	</svg>
);

const KeyboardIcon: React.FC = () => (
	<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
		<rect x="2" y="5" width="16" height="11" rx="2" stroke="var(--app-primary-text)" strokeWidth="1.375" fill="none" />
		<path d="M5 9h.01M8 9h.01M11 9h.01M14 9h.01M6 12.5h8" stroke="var(--app-primary-text)" strokeWidth="1.375" strokeLinecap="round" />
	</svg>
);

const PinModalShell: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => {
	const { t } = useTranslation();

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [onClose]);

	return (
		<CommonDialog onCloseDialog={onClose} noPadding borderRadius="21.67px" bottomSheetOnMobile>
			<div className={styles.modal}>
				<header className={styles.toolbar}>
					<button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t("account.web.close")}>
						<CrossIcon />
					</button>
					<h2 className={styles.title}>{title}</h2>
					<div className={styles.toolbarSpacer} />
				</header>
				<div className={styles.body}>{children}</div>
			</div>
		</CommonDialog>
	);
};

const TransactionPinModal: React.FC<TransactionPinModalProps> = ({
	submitText,
	isSubmitting = false,
	onSubmit,
	onForgetPin,
	onClose,
	showRemoteTokenLoad = false,
	initialRemoteTokenLoad = false,
}) => {
	const { t } = useTranslation();
	const hasPin = useSelector((state: RootState) => state.user.hasPin);
	const [showSuccessScreen, setShowSuccessScreen] = useState(false);
	const [isPinCreated, setIsPinCreated] = useState(false);

	if (showSuccessScreen) {
		return (
			<SuccessScreen
				title={t("pin.pinCreatedSuccessfullyTitle")}
				subtitle={t("pin.pinCreatedSuccessfullySubtitle")}
				onContinue={() => {
					setShowSuccessScreen(false);
					setIsPinCreated(true);
				}}
				buttonText={t("common.actions.continueKey")}
			/>
		);
	}

	if (hasPin || isPinCreated) {
		return (
			<PinModalShell title={t("pin.enterTransactionPin")} onClose={onClose}>
				<ValidatePinForm
					submitText={submitText ?? t("pin.pay")}
					isSubmitting={isSubmitting}
					onSubmit={onSubmit}
					onForgetPin={onForgetPin}
					showRemoteTokenLoad={showRemoteTokenLoad}
					initialRemoteTokenLoad={initialRemoteTokenLoad}
				/>
			</PinModalShell>
		);
	}

	return (
		<PinModalShell title={t("pin.createTransactionPin")} onClose={onClose}>
			<CreateNewPinForm onPinCreated={() => setShowSuccessScreen(true)} />
		</PinModalShell>
	);
};

const ValidatePinForm: React.FC<{
	submitText: string;
	isSubmitting: boolean;
	onSubmit: (pin: string, remoteTokenLoadForPayment?: boolean | null) => void | Promise<void>;
	onForgetPin: () => void;
	showRemoteTokenLoad: boolean;
	initialRemoteTokenLoad: boolean;
}> = ({ submitText, isSubmitting, onSubmit, onForgetPin, showRemoteTokenLoad, initialRemoteTokenLoad }) => {
	const { t } = useTranslation();
	const [pin, setPin] = useState("");
	const [isRemoteTokenLoadChecked, setIsRemoteTokenLoadChecked] = useState(initialRemoteTokenLoad);
	const [isDontShowAgainChecked, setIsDontShowAgainChecked] = useState(false);

	// Mirrors Flutter's _setRemoteTokenLoadingForPayment()
	// true  -> Remote token load is true (bez obzira na dont show again)
	// null  -> remote token load is false and Dont Show Again is false
	// false -> remote token load is false and don't show again true
	const remoteTokenLoadForPayment = (): boolean | null => {
		if (isRemoteTokenLoadChecked) return true;
		else if (!isRemoteTokenLoadChecked && !isDontShowAgainChecked) return null;
		else if (!isRemoteTokenLoadChecked && isDontShowAgainChecked) return false;
		return null;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (pin.length !== 4) {
			toast.error(t("errors.api.transactionPinValidationMsg"));
			return;
		}
		try {
			await onSubmit(pin, showRemoteTokenLoad ? remoteTokenLoadForPayment() : null);
		} catch (error: any) {
			toast.error(getErrorMessage(error?.data));
		}
	};

	return (
		<form className={styles.form} onSubmit={handleSubmit}>
			<div className={styles.otpRow}>
				<OtpInput
					value={pin}
					onChange={setPin}
					numInputs={4}
					inputType="password"
					shouldAutoFocus
					containerStyle={styles.otpContainer}
					renderInput={(props, idx) => (
						<input {...props} className={`${styles.otpInput} ${idx === pin.length && pin.length < 4 ? styles.otpInputFocus : ""}`} />
					)}
				/>
			</div>

			<button type="button" className={styles.forgotPinLink} onClick={onForgetPin}>
				{t("pin.forgotTransactionPin")}
			</button>

			{showRemoteTokenLoad && (
				<>
					<div className={styles.rtlCard}>
						<div className={styles.rtlMain}>
							<div className={styles.rtlTitleRow}>
								<KeyboardIcon />
								<span className={styles.rtlTitle}>{t("common.labels.remoteTokenLoad")}</span>
							</div>
							<p className={styles.rtlText}>{t("pin.toggleOnToAutomaticallySendToken")}</p>
						</div>
						<SwitchButton isActive={isRemoteTokenLoadChecked} onChange={val => setIsRemoteTokenLoadChecked(val)} size="md" activeColor="var(--app-secondary)" />
					</div>

					<SelectableItem
						title={t("common.labels.dontShowAgain")}
						borderColor="var(--app-primary-text-005)"
						borderRadius="20px"
						fontSize={12}
						padding="12px 12px"
						textColor="var(--app-primary-text-07)"
						onClick={() => setIsDontShowAgainChecked(prev => !prev)}
						rightContent={
							<SwitchButton isActive={isDontShowAgainChecked} onChange={val => setIsDontShowAgainChecked(val)} size="sm" activeColor="var(--app-secondary)" />
						}
					/>
				</>
			)}

			<Button type="submit" text={submitText} disabled={pin.length !== 4} loading={isSubmitting} loadingText={t("pin.processing")} />
		</form>
	);
};

const CreateNewPinForm: React.FC<{ onPinCreated: () => void }> = ({ onPinCreated }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [newPin, setNewPin] = useState("");
	const [confirmPin, setConfirmPin] = useState("");
	const [createPin, { isLoading }] = useCreatePinMutation();

	const mismatch = confirmPin.length === 4 && newPin !== confirmPin;

		const isSequentialPin = (value: string) => {
		const ascending = value.split("").every((d, i, arr) => i === 0 || Number(d) === Number(arr[i - 1]) + 1);
		const descending = value.split("").every((d, i, arr) => i === 0 || Number(d) === Number(arr[i - 1]) - 1);
		return ascending || descending;
	};

	const isRepeatedPin = (value: string) => value.split("").every(d => d === value[0]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		
		if (newPin.length !== 4) {
			toast.error(t("errors.api.transactionPinValidationMsg"));
			return;
		}
		if (newPin.length < 4) {
					toast.error(t("pin.pleaseEnterAllPinDigits"));
					return;
				}


				if (isSequentialPin(newPin)) {
					toast.error(t("pin.pinCannotBeSequential"));
					return;
				}

				if (isRepeatedPin(newPin)) {
					toast.error(t("pin.pinCannotHaveRepeatedDigits"));
					return;

				}
				if (newPin !== confirmPin) {
					toast.error(t("account.web.pinsDoNotMatch"));
					return;
				}
		try {
			await createPin({ pin: newPin }).unwrap();
			dispatch(updateUserPin({ hasPin: true }));
			trackSetTransactionPinSuccess();
			toast.success(t("errors.api.transactionPinSetSuccessfully"));
			onPinCreated();
		} catch (error: any) {
			trackSetTransactionPinFailed(describeError(error));
			toast.error(getErrorMessage(error?.data));
		}
	};

	return (
		<form className={styles.form} onSubmit={handleSubmit}>
			<div className={styles.pinGroup}>
				<label className={styles.pinGroupLabel}>{t("pin.newTransactionPin")}</label>
				<div className={styles.otpRow}>
					<OtpInput
						value={newPin}
						onChange={setNewPin}
						numInputs={4}
						inputType="password"
						shouldAutoFocus
						containerStyle={styles.otpContainer}
						renderInput={props => <input {...props} className={styles.otpInput} />}
					/>
				</div>
			</div>

			<div className={styles.pinGroup}>
				<label className={styles.pinGroupLabel}>{t("pin.reEneterNewTransactionPin")}</label>
				<div className={styles.otpRow}>
					<OtpInput
						value={confirmPin}
						onChange={setConfirmPin}
						numInputs={4}
						inputType="password"
						containerStyle={styles.otpContainer}
						renderInput={props => <input {...props} className={`${styles.otpInput} ${mismatch ? styles.otpInputError : ""}`} />}
					/>
				</div>
				{mismatch && <span className={styles.mismatchText}>{t("buyElectricity.web.pinsDoNotMatch")}</span>}
			</div>

			<Button
				type="submit"
				text={t("common.actions.continueKey")}
				disabled={newPin.length !== 4 || confirmPin.length !== 4 || newPin !== confirmPin}
				loading={isLoading}
				loadingText={t("pin.creatingPin")}
			/>
		</form>
	);
};

export default TransactionPinModal;
